import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { stringify as toQueryString } from '@AppModels/QueryStringUtils';
import { IDictionary, SURVEY_TYPES, SurveyTypes } from '../../extViewmodels';
import { useApiClientContext } from '../../surveys/context';
import { IOperationResultNoValue, ISurvey } from '../../surveys/models/Api';
import { LevStandardQueryMeta, reactQueryClient } from '../../web/ReactQueryProvider';

export const GET_SURVEY_BY_TOKEN_QUERY_KEY = 'getSurveyByTokenQuery';

export const useClientGetSurveyByToken = <T extends ISurvey>({
	accessToken,
	enabled = true,
	meta,
	refetchOnWindowFocus = false,
	options = {},
	retry,
	type,
}: {
	accessToken?: string;
	enabled?: boolean;
	meta?: LevStandardQueryMeta;
	refetchOnWindowFocus?: boolean;
	options?: IDictionary<string>;
	/** Replaces the default, which retries anything but a 4xx up to three times. */
	retry?: boolean | number;
	type?: SurveyTypes;
}) => {
	const { apiClient } = useApiClientContext();
	let urlBase = 'survey/byToken';

	if (type === SURVEY_TYPES.DonationSurvey) {
		urlBase = 'survey/donation/byToken';
	} else if (type === SURVEY_TYPES.EventRegistrationSurvey) {
		urlBase = 'survey/eventRegistration/byToken';
	} else if (type === SURVEY_TYPES.FileRequestSurvey) {
		urlBase = 'survey/fileRequest/byToken';
	}

	return useQuery({
		enabled,
		queryFn: () => {
			return apiClient.get<T>(`${urlBase}?${toQueryString({ token: accessToken, ...options })}`);
		},
		meta,
		queryKey: [GET_SURVEY_BY_TOKEN_QUERY_KEY, type, accessToken, options],
		refetchOnWindowFocus,
		// A 4xx (an expired or malformed link token, a withdrawn survey) won't change on retry.
		retry:
			retry ??
			((failureCount, error) => {
				const systemCode = (error as IOperationResultNoValue)?.systemCode ?? 0;
				return !(systemCode >= 400 && systemCode < 500) && failureCount < 3;
			}),
		// Keep the previously-loaded survey while a new access token (e.g. the draft-token swap on
		// submit) refetches, so consumers that gate rendering on `data` don't unmount/remount the
		// whole response subtree — that unmount is a flicker (and drops local response state).
		placeholderData: keepPreviousData,
	});
};

export const invalidateSurveyByToken = () => {
	reactQueryClient.invalidateQueries({ queryKey: [GET_SURVEY_BY_TOKEN_QUERY_KEY] });
};
